import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded font-medium text-sm transition-all duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[rgba(46,204,113,0.5)] focus-visible:ring-offset-1 focus-visible:ring-offset-[#0F0F0F] disabled:pointer-events-none disabled:opacity-40 select-none',
  {
    variants: {
      variant: {
        default:     'bg-[#2ECC71] text-[#0d1a0f] hover:bg-[#27ae60] shadow-sm hover:shadow-[0_0_12px_rgba(46,204,113,0.3)]',
        ghost:       'bg-transparent text-[#9CA3AF] hover:bg-[#2E2E2E] hover:text-[#F5F5F5]',
        outline:     'border border-[rgba(255,255,255,0.07)] bg-transparent text-[#9CA3AF] hover:bg-[#2E2E2E] hover:text-[#F5F5F5] hover:border-[rgba(255,255,255,0.13)]',
        subtle:      'bg-[#222222] text-[#9CA3AF] hover:bg-[#2E2E2E] hover:text-[#F5F5F5]',
        destructive: 'bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.2)] text-[#EF4444] hover:bg-[rgba(239,68,68,0.18)]',
        link:        'text-[#2ECC71] underline-offset-4 hover:underline h-auto p-0',
        accent:      'bg-[rgba(46,204,113,0.12)] border border-[rgba(46,204,113,0.25)] text-[#2ECC71] hover:bg-[rgba(46,204,113,0.2)]',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm:      'h-7 px-3 text-xs',
        lg:      'h-10 px-6',
        xl:      'h-11 px-8 text-base',
        icon:    'h-8 w-8 p-0',
        'icon-sm': 'h-6 w-6 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
